import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { generateLeaveApplicationPDF } from "../../../../../../Partials/Reports/Components/PurchaseReports/Components/leave/LeaveApplicationPrint";
import axios from "axios";

import { leaveRequestColumns, leaveRequestExportColumns } from "./LeaveRequestTableData";

import DataTable from "../../../../../../Common/DataTable/DataTable";
import DataTableHeader from "../../../../../../Common/DataTableHeader/DataTableHeader";
import DataTableFooter from "../../../../../../Common/DataTableFooter/DataTableFooter";
import { leaveRequestApi } from "../../../../../../api/leaveRequestApi";
import { REACT_APP_API_URL } from "../../../../../../env";

const LeaveRequestTable = ({ managerScope = false, managerId = "" }) => {
  const [rows, setRows] = useState([]);
  const [exportRows, setExportRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [managerRemarks, setManagerRemarks] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const printRef = useRef(null);
  const [printData, setPrintData] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);

  const isReportMode = true;

  const onNameChange = (value) => {
    setFilterName(value);
  };

  const onMonthChange = (value) => {
    setFilterMonth(value);
  };

  const onStatusChange = (value) => {
    setFilterStatus(value);
  };

  const hasPending = useMemo(() => {
    return rows.some(
      (r) =>
        r.status?.props?.children?.toLowerCase() === "pending"
    );
  }, [rows]);

  const renderStatusBadge = (status) => {
    const normalized = String(status || "pending").toLowerCase();
    if (normalized === "approved") {
      return <span className="badge bg-success">Approved</span>;
    }
    if (normalized === "rejected") {
      return <span className="badge bg-danger">Rejected</span>;
    }
    return <span className="badge bg-warning text-dark">Pending</span>;
  };

  const handleUpdateStatus = useCallback(async () => {
    if (!selectedRequestId || !selectedStatus) return;

    if (
      String(selectedStatus).toLowerCase() === "rejected" &&
      !managerRemarks.trim()
    ) {
      alert("Remarks are mandatory when rejecting a leave request");
      return;
    }

    try {
      setUpdatingId(selectedRequestId);

      const response = await leaveRequestApi.updateLeaveRequestStatus(
        selectedRequestId,
        {
          status: selectedStatus,
          managerRemarks: managerRemarks?.trim() || null,
        }
      );

      window.alert(
        response?.data?.message ||
        `Leave request ${selectedStatus} successfully`
      );

      setRows((prev) =>
        prev.map((r) =>
          r.__id === selectedRequestId
            ? {
              ...r,
              status: renderStatusBadge(selectedStatus),
              managerRemarks: managerRemarks?.trim() || "-",
              actions: <span className="text-muted">-</span>,
            }
            : r
        )
      );

      setShowRemarksModal(false);
    } catch (err) {
      setError(err?.message || "Failed to update leave status");
    } finally {
      setUpdatingId(null);
    }
  }, [selectedRequestId, selectedStatus, managerRemarks]);

  const renderActions = (requestId, status) => {
    const normalized = String(status || "pending").toLowerCase();
    if (normalized === "approved" || normalized === "rejected") {
      return <span className="text-muted">-</span>;
    }

    const openRemarksModal = (requestId, status) => {
      setSelectedRequestId(requestId);
      setSelectedStatus(status);
      setManagerRemarks("");
      setShowRemarksModal(true);
    };

    return (
      <div className="btn-group" role="group">
        <button
          type="button"
          className="btn btn-outline-success"
          onClick={() => openRemarksModal(requestId, "approved")}
          disabled={updatingId === requestId}
          title="Approve"
        >
          <i className="bi bi-check2"></i>
        </button>
        <button
          type="button"
          className="btn btn-outline-danger deleterow"
          onClick={() => openRemarksModal(requestId, "rejected")}
          disabled={updatingId === requestId}
          title="Reject"
        >
          <i className="bi bi-x"></i>
        </button>
      </div>
    );
  };


  const formatDate = (date) => {
    if (!date) return "-";

    let parsed = new Date(date);

    // Handle common non-ISO formats like DD/MM/YYYY or DD-MM-YYYY
    if (Number.isNaN(parsed.getTime()) && typeof date === "string") {
      const trimmed = date.trim();
      const match = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
      if (match) {
        const day = Number(match[1]);
        const month = Number(match[2]);
        const year = Number(match[3]);
        parsed = new Date(Date.UTC(year, month - 1, day));
      }
    }

    if (Number.isNaN(parsed.getTime())) return "-";

    return parsed.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: printData
      ? `Leave_Application_${printData.leave_id}`
      : "Leave_Application",
  });


  const printLeaveApplication = async (leaveId) => {
    try {
      setPrintLoading(true);

      const res = await axios.get(
        `${REACT_APP_API_URL}api/hrms/leave/application/${leaveId}`
      );

      if (!res?.data?.data) {
        throw new Error("Invalid response");
      }

      generateLeaveApplicationPDF(res.data.data);

    } catch (err) {
      window.alert("Failed to generate leave application PDF");
    } finally {
      setPrintLoading(false);
    }
  };

  useEffect(() => {
    console.log("printData:", printData);
    console.log("printRef:", printRef.current);
    if (printData && printRef.current) {
      handlePrint();
    }
  }, [printData]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = managerScope
          ? await leaveRequestApi.getStaffLeaveRequests(managerId)
          : await leaveRequestApi.getAllLeaveRequests();
        const allItems = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.items)
            ? response.items
            : Array.isArray(response?.leaveRequests)
              ? response.leaveRequests
              : Array.isArray(response?.results)
                ? response.results
                : Array.isArray(response)
                  ? response
                  : [];
        const items = allItems;
        if (!isMounted) return;
        const uiRows = [];
        const exportData = [];

        items
          .sort((a, b) => {
            const aDate = new Date(
              a?.created_at ||
              a?.applied_on ||
              a?.appliedOn ||
              a?.application_date ||
              a?.applied_date ||
              a?.apply_date ||
              a?.date ||
              0
            ).getTime();
            const bDate = new Date(
              b?.created_at ||
              b?.applied_on ||
              b?.appliedOn ||
              b?.application_date ||
              b?.applied_date ||
              b?.apply_date ||
              b?.date ||
              0
            ).getTime();
            return bDate - aDate;
          })
          .forEach((item) => {

            const requestId =
              item.id || item._id || item.request_id || item.requestId;

            const appliedOnRaw =
              item.created_at ||
              item.applied_on ||
              item.appliedOn ||
              item.application_date ||
              item.applied_date ||
              item.apply_date ||
              item.date ||
              null;

            const applyingTo =
              item.applying_to ||
              item.ApplyingTo ||
              "-";

            const employeeName =
              item.employee_name ||
              item.employe_name ||
              item.employeeName ||
              item.emp_name ||
              item.staff_name ||
              item.staffName ||
              item.name ||
              item?.employee?.name ||
              item?.employee?.employee_name ||
              item?.staff?.name ||
              item?.user?.name ||
              "Unknown";

            const leaveType =
              item.leave_type || item.leaveType || item.type || "-";

            const fromDate =
              item.leave_from || item.from || item.start_date || "";

            const toDate =
              item.leave_to || item.to || item.end_date || "";

            const totalDays =
              item.total_days || item.totalDays || 1;

            const reason = item.reason || "";

            const managerRemarks =
              item.manager_remarks ||
              item.ManagerRemarks ||
              "-";
            const managerRemarksText = String(managerRemarks);

            const statusRaw =
              item.status || item.leave_status || "pending";

            const attachmentPath = item.attachment || item.AttachmentPath || null;

            /* UI ROW */
            uiRows.push({
              __id: requestId,
              applying_to: item.applying_to || item.ApplyingTo,
              employeeName: <span className="fw-semibold">{employeeName}</span>,
              appliedOnRaw,
              appliedOn: (
                <span className="text-muted small">
                  {appliedOnRaw ? formatDate(appliedOnRaw) : "-"}
                </span>
              ),
              applyingTo: applyingTo,
              leaveType: <span className="badge bg-primary">{leaveType}</span>,
              from: fromDate,
              to: toDate,
              totalDays: <span className="badge bg-info">{totalDays}</span>,
              reason,
              managerRemarks: (
                <span
                  title={managerRemarksText}
                  className={managerRemarksText === "-" ? "text-muted" : ""}
                >
                  {managerRemarksText.length > 30
                    ? managerRemarksText.slice(0, 30) + "..."
                    : managerRemarksText}
                </span>
              ),
              status: renderStatusBadge(statusRaw),
              print: managerScope ? (
                <span className="text-muted">-</span>
              ) : (
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => printLeaveApplication(requestId)}
                  disabled={printLoading}
                  title="Print Leave Application"
                >
                  <i className="bi bi-printer"></i>
                </button>
              ),

              actions: renderActions(requestId, statusRaw),
              attachment: attachmentPath ? (
                <a
                  href={`${REACT_APP_API_URL}${attachmentPath}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-secondary"
                >
                  View
                </a>
              ) : (
                <span className="text-muted small">No File</span>
              ),
            });

            /* EXPORT ROW */
            exportData.push({
              employeeName,
              appliedOn: appliedOnRaw ? formatDate(appliedOnRaw) : "-",
              leaveType,
              from: fromDate,
              to: toDate,
              totalDays,
              reason,
              status: statusRaw,
            });
          });
        setRows(uiRows);
        setExportRows(exportData);

      } catch (err) {
        if (!isMounted) return;
        setError(
          typeof err === "string"
            ? err
            : err?.message || "Failed to load leave requests"
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [managerScope, managerId]);

  const tableData = useMemo(() => {
    return rows.filter((row) => {
      let match = true;

      /* Exclude MANAGER leaves */
      if (String(row.applying_to || "").toUpperCase().includes("MANAGER")) {
        return false;
      }

      /* Employee Name Filter */
      if (filterName) {
        const name =
          row.employeeName?.props?.children?.toLowerCase() || "";
        match =
          match &&
          name.includes(filterName.toLowerCase());
      }

      /* Month Filter (based on Applied On / created_at) */
      if (filterMonth) {
        if (!row.appliedOnRaw) return false;

        const date = new Date(row.appliedOnRaw);
        if (isNaN(date.getTime())) return false;

        const rowMonth = date.toISOString().slice(0, 7);
        match = match && rowMonth === filterMonth;
      }

      /* Status Filter */
      if (filterStatus) {
        const status =
          row.status?.props?.children?.toLowerCase() || "";
        match =
          match &&
          status === filterStatus.toLowerCase();
      }

      return match;
    });
  }, [rows, filterName, filterMonth, filterStatus]);

  const visibleColumns = useMemo(() => {
    const baseColumns = hasPending
      ? leaveRequestColumns
      : leaveRequestColumns.filter((c) => c.accessor !== "actions");

    if (managerScope) {
      return baseColumns.filter(
        (c) =>
          c.accessor !== "print" &&
          c.accessor !== "applyingTo"
      );
    }

    return baseColumns;
  }, [hasPending, managerScope]);

  return (
    <>
      <div className="card mb-3 leave-surface-card leave-filter-card">
        <div className="card-body leave-surface-body">
          <div className="row g-3 align-items-end">

            <div className="col-md-4">
              <label className="form-label">Employee Name</label>
              <input
                type="text"
                className="form-control"
                value={filterName}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="Search employee"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Leave Applied Month</label>
              <input
                type="month"
                className="form-control"
                value={filterMonth}
                onChange={(e) => onMonthChange(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filterStatus}
                onChange={(e) => onStatusChange(e.target.value)}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="col-md-2 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setFilterName("");
                  setFilterMonth("");
                  setFilterStatus("");
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      <DataTableHeader columns={leaveRequestExportColumns}
        data={exportRows} />
      {error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : null}

      <div className="leave-table-shell leave-admin-table-shell">
        <DataTable
          columns={visibleColumns}
          data={tableData}
          loading={loading}
          reportMode={true}
          disableSorting={true}
          enablePagination={false}
          onExportData={setExportRows}
        />
      </div>

      {!isReportMode && (
        <DataTableFooter dataT={tableData} />
      )}

      {showRemarksModal && (
        <>
          <div
            className="modal show d-block leave-modal"
            tabIndex="-1"
            style={{ zIndex: 1055 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {selectedStatus === "approved"
                      ? "Approve Leave"
                      : "Reject Leave"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowRemarksModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <label className="form-label">
                    Remarks
                    {selectedStatus === "rejected" && (
                      <span className="text-danger"> *</span>
                    )}
                  </label>

                  <textarea
                    className="form-control"
                    rows="4"
                    value={managerRemarks}
                    onChange={(e) => setManagerRemarks(e.target.value)}
                    placeholder={
                      selectedStatus === "approved"
                        ? "Optional remarks (if any)"
                        : "Remarks are required"
                    }
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowRemarksModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    className={`btn ${selectedStatus === "approved"
                      ? "btn-success"
                      : "btn-danger"
                      }`}
                    onClick={handleUpdateStatus}
                    disabled={
                      updatingId === selectedRequestId ||
                      (String(selectedStatus).toLowerCase() === "rejected" &&
                        !managerRemarks.trim())
                    }
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            className="modal-backdrop show"
            style={{ zIndex: 1050, pointerEvents: "none" }}
          />
        </>
      )}
    </>
  );
};

export default LeaveRequestTable;

